/**
 * Document-filter service for SubContractorHub (Story SCH-2.4).
 *
 * Wraps the discover-document-labels branch + list + bulk-save endpoints.
 * Mirrors adderMappingService.ts conventions: shared fetchJson helper,
 * snake_case ↔ camelCase translation at the boundary, discriminated
 * union result types.
 */

import { supabase } from '@/lib/supabaseClient';
import type {
  AutoMatchSuggestion,
  BulkSaveDocumentFiltersRequest,
  BulkSaveDocumentFiltersResult,
  CreateDocumentCategoryAndFilterRequest,
  CreateDocumentCategoryAndFilterResult,
  DiscoverDocumentLabelsResponse,
  DiscoverDocumentLabelsResult,
  DocumentCategoryRow,
  DocumentFilterRow,
  DocumentMirrorMode,
  ListDocumentFiltersResult,
} from '@/types/integrations/documentFilter';

const BACKEND_URL =
  import.meta.env.VITE_PYTHON_BACKEND_URL || 'http://localhost:8000';

async function authHeader(): Promise<HeadersInit> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return {
    Authorization: `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
}

interface ApiEnvelope<TData> {
  data: TData | null;
  error: { code: string; message: string; details?: Record<string, unknown> } | null;
  meta: unknown;
}

async function fetchJson<TData>(
  path: string,
  init: RequestInit,
): Promise<ApiEnvelope<TData>> {
  const headers = await authHeader();
  const response = await fetch(`${BACKEND_URL}${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers ?? {}) },
  });
  if (!response.ok && response.status !== 200) {
    let detail: string | undefined;
    try {
      const json = await response.json();
      detail = typeof json?.detail === 'string' ? json.detail : undefined;
    } catch {
      // ignore parse error
    }
    throw new Error(detail || `Request failed with status ${response.status}`);
  }
  return (await response.json()) as ApiEnvelope<TData>;
}

// ─── Raw → camelCase mappers ─────────────────────────────────────────────
interface RawDocumentFilterRow {
  id: string;
  tenant_integration_id: string;
  external_label: string;
  is_allowed: boolean;
  is_satellite_picture_source: boolean;
  mirror_mode: DocumentMirrorMode;
  joules_document_category_id: number | null;
  joules_document_category_name: string | null;
  created_at: string | null;
  updated_at: string | null;
}

function _mapRow(raw: RawDocumentFilterRow): DocumentFilterRow {
  return {
    id: raw.id,
    tenantIntegrationId: raw.tenant_integration_id,
    externalLabel: raw.external_label,
    isAllowed: raw.is_allowed,
    isSatellitePictureSource: raw.is_satellite_picture_source,
    mirrorMode: raw.mirror_mode,
    joulesDocumentCategoryId: raw.joules_document_category_id ?? null,
    joulesDocumentCategoryName: raw.joules_document_category_name ?? null,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

interface RawDocumentCategoryRow {
  id: number;
  tenant_id: string | null;
  name: string;
  display_label: string;
  icon: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
}

function _mapCategoryRow(raw: RawDocumentCategoryRow): DocumentCategoryRow {
  return {
    id: raw.id,
    tenantId: raw.tenant_id,
    name: raw.name,
    displayLabel: raw.display_label,
    icon: raw.icon,
    sortOrder: raw.sort_order,
    isActive: raw.is_active,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

interface RawAutoMatchResult {
  joules_target_id: number;
  confidence: number;
}

function _mapSuggestion(raw: RawAutoMatchResult | null): AutoMatchSuggestion | null {
  if (!raw) return null;
  return {
    joulesTargetId: raw.joules_target_id,
    confidence: raw.confidence,
  };
}

const DISCOVER_ERROR_CODES = [
  'integration_not_found',
  'auth_failed',
  'connection_test_failed',
  'internal_error',
] as const;
const LIST_ERROR_CODES = ['integration_not_found', 'internal_error'] as const;
const BULK_SAVE_ERROR_CODES = [
  'integration_not_found',
  'multiple_satellite_sources',
  'internal_error',
] as const;

function _normalizeError<TCode extends string>(
  raw: { code: string; message: string; details?: Record<string, unknown> } | null,
  knownCodes: readonly TCode[],
): { code: TCode; message: string; details?: Record<string, unknown> } {
  if (!raw) {
    return { code: 'internal_error' as TCode, message: 'Unknown error' };
  }
  const code = (knownCodes as readonly string[]).includes(raw.code)
    ? (raw.code as TCode)
    : ('internal_error' as TCode);
  return { code, message: raw.message, details: raw.details };
}

interface RawDiscoverDocumentLabelsResponse {
  discovered_labels: string[];
  existing_filters: RawDocumentFilterRow[];
  joules_categories?: RawDocumentCategoryRow[];               // SCH-2.5 (default empty for back-compat)
  suggestions?: Record<string, RawAutoMatchResult | null>;    // SCH-2.5
}

function _mapDiscoverResponse(
  raw: RawDiscoverDocumentLabelsResponse,
): DiscoverDocumentLabelsResponse {
  return {
    discoveredLabels: raw.discovered_labels,
    existingFilters: raw.existing_filters.map(_mapRow),
    joulesCategories: (raw.joules_categories ?? []).map(_mapCategoryRow),
    suggestions: Object.fromEntries(
      Object.entries(raw.suggestions ?? {}).map(([key, value]) => [
        key,
        _mapSuggestion(value),
      ]),
    ),
  };
}

export const documentFilterService = {
  // ─── GET /discover-catalog?include=document_labels ─────────────────────
  async discoverDocumentLabels(): Promise<DiscoverDocumentLabelsResult> {
    const env = await fetchJson<RawDiscoverDocumentLabelsResponse>(
      '/api/v1/integrations/subcontractorhub/discover-catalog?include=document_labels',
      { method: 'GET' },
    );
    if (env.data) {
      return { data: _mapDiscoverResponse(env.data), error: null };
    }
    return { data: null, error: _normalizeError(env.error, DISCOVER_ERROR_CODES) };
  },

  // ─── GET /document-filters ─────────────────────────────────────────────
  async listDocumentFilters(): Promise<ListDocumentFiltersResult> {
    const env = await fetchJson<{ rows: RawDocumentFilterRow[] }>(
      '/api/v1/integrations/subcontractorhub/document-filters',
      { method: 'GET' },
    );
    if (env.data) {
      return {
        data: { rows: env.data.rows.map(_mapRow) },
        error: null,
      };
    }
    return { data: null, error: _normalizeError(env.error, LIST_ERROR_CODES) };
  },

  // ─── POST /document-filters/bulk-save ──────────────────────────────────
  async bulkSaveDocumentFilters(
    req: BulkSaveDocumentFiltersRequest,
  ): Promise<BulkSaveDocumentFiltersResult> {
    const body = {
      rows: req.rows.map((r) => ({
        external_label: r.externalLabel,
        is_allowed: r.isAllowed,
        is_satellite_picture_source: r.isSatellitePictureSource,
        joules_document_category_id: r.joulesDocumentCategoryId,
      })),
    };

    const env = await fetchJson<{ rows: RawDocumentFilterRow[] }>(
      '/api/v1/integrations/subcontractorhub/document-filters/bulk-save',
      { method: 'POST', body: JSON.stringify(body) },
    );
    if (env.data) {
      return {
        data: { rows: env.data.rows.map(_mapRow) },
        error: null,
      };
    }
    return { data: null, error: _normalizeError(env.error, BULK_SAVE_ERROR_CODES) };
  },

  // ─── POST /document-filters/with-new-category (SCH-2.5) ────────────────
  async createDocumentFilterWithNewCategory(
    req: CreateDocumentCategoryAndFilterRequest,
  ): Promise<CreateDocumentCategoryAndFilterResult> {
    const body: Record<string, unknown> = {
      external_label: req.externalLabel,
      category: {
        name: req.category.name,
        display_label: req.category.displayLabel,
        icon: req.category.icon ?? null,
        is_active: req.category.isActive ?? true,
      },
      is_satellite_picture_source: req.isSatellitePictureSource ?? false,
    };
    if (req.category.sortOrder !== undefined) {
      (body.category as Record<string, unknown>).sort_order = req.category.sortOrder;
    }

    const env = await fetchJson<{
      category: RawDocumentCategoryRow;
      filter: RawDocumentFilterRow;
    }>(
      '/api/v1/integrations/subcontractorhub/document-filters/with-new-category',
      { method: 'POST', body: JSON.stringify(body) },
    );
    if (env.data) {
      return {
        data: {
          category: _mapCategoryRow(env.data.category),
          filter: _mapRow(env.data.filter),
        },
        error: null,
      };
    }
    return {
      data: null,
      error: _normalizeError(env.error, ['integration_not_found', 'internal_error'] as const),
    };
  },
};
